import React from 'react';

export interface RevenueVsExpenseData {
  month: string;
  revenue: number;
  expense: number;
}

export interface RevenueVsExpenseProps {
  data: RevenueVsExpenseData[];
  isLoading?: boolean;
  height?: number;
}

const RevenueVsExpense: React.FC<RevenueVsExpenseProps> = ({ data, isLoading, height = 300 }) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue vs Expenses</h2>
        <div className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue vs Expenses</h2>
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          No data available
        </div>
      </div>
    );
  }

  const maxValue = Math.max(
    ...data.map(d => Math.max(d.revenue, d.expense)),
    0
  );
  const chartHeight = height - 100;
  const barWidth = Math.max(20, (800 / data.length) - 15);
  const groupSpacing = 5;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Revenue vs Expenses</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span className="text-sm text-gray-600">Revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-red-500"></div>
            <span className="text-sm text-gray-600">Expenses</span>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <div className="flex items-end justify-between gap-2" style={{ height: `${chartHeight}px` }}>
          {data.map((item, index) => {
            const revenueHeight = maxValue > 0 ? (item.revenue / maxValue) * chartHeight : 0;
            const expenseHeight = maxValue > 0 ? (item.expense / maxValue) * chartHeight : 0;
            const netProfit = item.revenue - item.expense;
            
            return (
              <div
                key={index}
                className="flex-1 flex flex-col items-center group relative"
                style={{ minWidth: `${barWidth * 2 + groupSpacing}px` }}
              >
                <div className="w-full flex items-end justify-center gap-1" style={{ height: `${chartHeight}px` }}>
                  <div
                    className="flex-1 rounded-t transition-all hover:opacity-80 cursor-pointer relative"
                    style={{
                      height: `${revenueHeight}px`,
                      backgroundColor: '#3b82f6',
                      minHeight: revenueHeight > 0 ? '4px' : '0',
                    }}
                  >
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      Revenue: €{item.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div
                    className="flex-1 rounded-t transition-all hover:opacity-80 cursor-pointer relative"
                    style={{
                      height: `${expenseHeight}px`,
                      backgroundColor: '#ef4444',
                      minHeight: expenseHeight > 0 ? '4px' : '0',
                    }}
                  >
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      Expense: €{item.expense.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-600 text-center truncate w-full" title={item.month}>
                  {item.month}
                </div>
                <div className={`text-xs mt-1 font-medium ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {netProfit >= 0 ? '+' : ''}€{netProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RevenueVsExpense;

